<template>
    <v-expansion-panels multiple :value="open" _hover flat class="fl-filter-vertical">
        <v-expansion-panel v-for="filter in allFilters" :key="filter.name" role="tabpanel">
            <v-expansion-panel-header :expand-icon="mdiChevronDown" class="font-weight-bold" role="button">
                {{ filter.displayName }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
                <template v-if="filter.name === 'cat'">
                    <cat-filter v-model="selectedFilters[filter.name]" :filter="filter" @input="onFilterValuesChange(filter, $event)" />
                </template>

                <template v-else-if="filter.type === 'range-slider'">
                    <range-slider
                        :value="selectedFilters[filter.name]"
                        :filter="filter"
                        :currency-symbol="currencySymbol"
                        @input="onFilterValuesChange(filter, $event)"
                    />
                </template>

                <template v-else-if="['color', 'image'].includes(filter.type)">
                    <color-image-filter
                        :value="selectedFilters[filter.name]"
                        :filter="filter"
                        @input="onFilterValuesChange(filter, $event)"
                    />
                </template>

                <template v-else>
                    <label-filter :value="selectedFilters[filter.name]" :filter="filter" @input="onFilterValuesChange(filter, $event)" />
                </template>
            </v-expansion-panel-content>
            <v-divider />
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<script lang="ts">
import { mdiCheckboxBlankOutline, mdiCheckboxMarked, mdiChevronDown } from '@mdi/js';
import { Filter, FINDOLOGICSearchAPIJSONResponse10 } from '../../../../declarations/result';
import { PropType } from 'vue';
import RangeSlider from './Filter/RangeSlider.vue';
import { Vue } from 'vue-property-decorator';
import LabelFilter from './Filter/LabelFilter.vue';
import CatFilter from './Filter/CatFilter.vue';
import ColorImageFilter from './Filter/ColorImageFilter.vue';
import { FilterValues, Params } from '../../api/api';
import { forEach, range as _range } from 'lodash-es';
import { analyticsTracker as flAnalyticsTracker } from '@findologic/js-common';
import Request from '../../../../../src/modules/request.js';
import { EventNames } from '../../../Common/enums';

export default Vue.extend({
    name: 'FilterVertical',
    components: { LabelFilter, RangeSlider, ColorImageFilter, CatFilter },
    props: {
        value: {
            type: Object as PropType<Params['attrib']>,
            default() {
                return {};
            },
        },
        filters: {
            type: Object as PropType<FINDOLOGICSearchAPIJSONResponse10['result']['filters']>,
            required: true,
        },
        currencySymbol: {
            type: String,
            required: true,
        },
        analyticsTracker: {
            type: Object as PropType<flAnalyticsTracker>,
            required: true,
        },
        operation: {
            type: String,
            required: true,
        },
    },
    computed: {
        open(): number[] {
            if (this.$vuetify.breakpoint.smAndDown) {
                return [];
            }

            const selectedFilterIndices = Array.from(this.allFilters.entries())
                .filter(([, filter]: [number, Filter]) => filter.name in this.selectedFilters)
                .map(([i]: [number, Filter]) => i);
            const mainFilterIndices = _range(this.filters.main.length);

            return mainFilterIndices.concat(selectedFilterIndices);
        },
        allFilters(): Filter[] {
            return [...this.filters.main, ...this.filters.other].filter(
                (filter: Filter): boolean => filter.values.length > 0 || !!filter.noAvailableFiltersText
            );
        },
        selectedFilters: {
            get(): Params['attrib'] {
                return this.value;
            },
            set(attrib: Params['attrib']) {
                this.$emit('input', attrib);
            },
        },
    },
    methods: {
        onFilterValuesChange(filter: Filter, values: FilterValues) {
            this.selectedFilters = { ...this.selectedFilters, ...{ [filter.name]: values } };
            const eventCategory = `FINDOLOGIC Filter Click: ${this.operation === Request.TYPE_SEARCH ? 'Search' : 'Navigation'}`;

            this.analyticsTracker.trackEvent('findologicEvent', eventCategory, filter.name, values[0], null, {
                event: EventNames.FILTER_CLICK,
                operation: this.operation,
                filter_name: filter.name,
                filter_value: values[0],
            });
        },
    },
    data() {
        return {
            mdiChevronDown,
            mdiCheckboxBlankOutline,
            mdiCheckboxMarked,
        };
    },
});
</script>

<style scoped>
>>> .v-expansion-panel.v-expansion-panel--active.v-item--active {
    margin-top: 0;
}
>>> .px-0 .v-expansion-panel-content__wrap {
    padding-left: 0;
    padding-right: 0;
}
</style>
